import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Loader,
  Notice
} from '@deque/cauldron-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  deleteAdminFeatureFlag,
  updateAdminFeatureFlag,
  type AdminFeatureFlag,
  type SSOConfig
} from '~/modules/common/api-client';
import type { AuthUser } from '~/modules/common/contexts/auth';
import { useGlobalToast } from '~/modules/common/contexts/globalToast';
import type { ErrorOrNull, OrNull } from '~/typings/utils';
import FeatureFlagTableAdmin, {
  FeatureFlagState,
  type OnStateChangePayload
} from '../components/FeatureFlagsTableAdmin';
import styles from './AdminFeatureFlags.css';

interface ErrorMessageProps {
  title: string;
  children: React.ReactNode;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ title, children }) => {
  return (
    <div>
      <details>
        <summary>
          <strong>{title}</strong>
        </summary>
        <pre>
          <code>{children}</code>
        </pre>
      </details>
    </div>
  );
};

interface FeatureStateChangeConfirmationProps {
  show: boolean;
  payload: OnStateChangePayload;
  disabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const FeatureStateChangeConfirmation: React.FC<
  FeatureStateChangeConfirmationProps
> = ({ show, payload, disabled = false, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const { id, newState } = payload;

  const content = React.useMemo(
    () => ({
      [FeatureFlagState.ENABLED]: t(
        'Do you want to enable feature: {{id}} globally?',
        { id }
      ),
      [FeatureFlagState.DISABLED]: t(
        'Do you want to disable feature: {{id}} globally?',
        { id }
      ),
      [FeatureFlagState.NONE]: t(
        'Do you want to remove feature: {{id}} flag override and use the code default state?',
        { id }
      )
    }),
    [t, id]
  );

  return (
    <Alert
      show={show}
      heading={t('Global feature flag state change confirmation')}
      variant="default"
      onClose={disabled ? undefined : onCancel}
    >
      <AlertContent>
        <p>{content[newState]}</p>
      </AlertContent>
      <AlertActions>
        <Button disabled={disabled} onClick={onConfirm}>
          {t('Confirm')}
        </Button>
        <Button disabled={disabled} onClick={onCancel}>
          {t('Cancel')}
        </Button>
      </AlertActions>
    </Alert>
  );
};

interface OnStateChangeArgs {
  id: string;
  state: boolean;
}

export interface AdminFeatureFlagsContainerProps {
  narrow: boolean;
  user: AuthUser;
  features: AdminFeatureFlag[];
  featuresLoading: boolean;
  featuresError: ErrorOrNull;
  ssoConfig: OrNull<SSOConfig>;
  ssoConfigLoading: boolean;
  onRefreshFeatures: () => void;
}

const AdminFeatureFlagsContainer: React.FC<AdminFeatureFlagsContainerProps> = ({
  narrow,
  user,
  features,
  featuresLoading,
  featuresError,
  ssoConfig,
  ssoConfigLoading,
  onRefreshFeatures
}) => {
  const { t } = useTranslation();
  const { setContents: setToastContents } = useGlobalToast();
  const [pendingChange, setPendingChange] =
    React.useState<OrNull<OnStateChangePayload>>(null);
  const [isStateUpdating, setIsStateUpdating] = React.useState(false);
  const loading = featuresLoading || ssoConfigLoading;

  const loaderRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (loading && element) {
        element.focus();
      }
    },
    [loading]
  );

  const handleUpdateOverride = async (args: OnStateChangeArgs) => {
    const { id, state } = args;
    try {
      await updateAdminFeatureFlag({
        defaultState: state,
        featureId: id,
        token: user.token ?? ''
      });
      onRefreshFeatures();
      setToastContents(
        t('Feature flag state updated successfully.'),
        'confirmation'
      );
    } catch {
      setToastContents(
        t('Failed to update feature flag state. Please try again.'),
        'error'
      );
    } finally {
      setPendingChange(null);
    }
  };

  const handleRemoveOverride = async (featureId: string) => {
    try {
      await deleteAdminFeatureFlag({
        featureId,
        token: user.token ?? ''
      });
      onRefreshFeatures();
      setToastContents(t('Feature flag override was deleted.'), 'confirmation');
    } catch {
      setToastContents(
        t('Failed to delete feature flag override. Please try again.'),
        'error'
      );
    } finally {
      setPendingChange(null);
    }
  };

  const actions: Record<FeatureFlagState, (id: string) => Promise<void>> = {
    [FeatureFlagState.ENABLED]: id => handleUpdateOverride({ id, state: true }),
    [FeatureFlagState.DISABLED]: id =>
      handleUpdateOverride({ id, state: false }),
    [FeatureFlagState.NONE]: id => handleRemoveOverride(id)
  };

  const handleStateChangeConfirmed = async () => {
    if (!pendingChange) {
      return;
    }
    setIsStateUpdating(true);
    try {
      const { newState, id } = pendingChange;
      await actions[newState](id);
    } finally {
      setIsStateUpdating(false);
    }
  };

  const handleStateChangeCanceled = () => {
    setPendingChange(null);
  };

  const handleStateChange = (payload: OnStateChangePayload) => {
    setPendingChange(payload);
  };

  return (
    <>
      <Notice title={t('Caution')} type="caution">
        <p>
          {t(
            'Do not manually update feature flag states except in urgent feature-disable scenarios for QA and Prod environments. Use code changes whenever possible.'
          )}
        </p>
      </Notice>
      <div className={styles.container}>
        {loading && (
          <Loader label={t('Loading features')} tabIndex={-1} ref={loaderRef} />
        )}
        {featuresError && (
          <ErrorMessage title={t('Unable to load features')}>
            {featuresError.message}
          </ErrorMessage>
        )}
        {!loading && !featuresError && (
          <div>
            <FeatureFlagTableAdmin
              featureFlags={features}
              isNarrow={narrow}
              ssoConfig={ssoConfig}
              isUpdatingDisabled={isStateUpdating}
              onFeatureStateChange={handleStateChange}
            />
          </div>
        )}
      </div>
      {pendingChange !== null && (
        <FeatureStateChangeConfirmation
          show
          disabled={isStateUpdating}
          onCancel={handleStateChangeCanceled}
          onConfirm={handleStateChangeConfirmed}
          payload={pendingChange}
        />
      )}
    </>
  );
};

export default AdminFeatureFlagsContainer;
